/**
 * Admin Shelly Devices - Manage Shelly Button integrations
 * Features:
 * - Personal buttons (assigned to users, max 2 per user)
 * - Rental buttons (assigned to club+court, clips for all users on court)
 * - User search with autocomplete
 * - Quick user creation
 * - Signal testing and logs
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Radio, Plus, Trash2, ChevronLeft, RefreshCw, User, UserPlus, UserMinus,
  Copy, Check, AlertCircle, Building2, X, AlertTriangle, Bell,
  Search, MapPin, Users, ChevronDown, ChevronUp, XCircle, Calendar, Edit, Play,
  Wifi, Clock, Phone, Mail, Hash, Loader2, Activity
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// ===========================================
// DEVICE CARD COMPONENT
// ===========================================
const DeviceCard = ({ device, clubs, onDelete, onTest, onAssign, onUnassign, onEdit, onViewLogs, onShowInstructions }) => {
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Edit form state
  const [editData, setEditData] = useState({
    device_name: device.device_name || '',
    club_id: device.club_id || '',
    court_id: device.court_id || '',
    button_type: device.button_type || 'personal',
    is_active: device.is_active !== false
  });
  
  const editSelectedClub = useMemo(() => {
    if (editData.club_id) {
      return clubs.find(c => c.id === editData.club_id) || null;
    }
    return null;
  }, [editData.club_id, clubs]);

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(device.webhook_url);
    setCopied(true);
    toast.success('URL copiada');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditClubChange = (clubId) => {
    setEditData({ ...editData, club_id: clubId, court_id: '' });
  };

  const handleSaveEdit = async () => {
    await onEdit(device.device_id, editData);
    setShowEditModal(false);
  };

  // Determine display based on button type
  const isRental = device.button_type === 'rental';
  const isAssigned = isRental ? (device.club_id && device.court_id) : device.assigned_user_id;

  // Get display info
  const getAssignmentDisplay = () => {
    if (isRental) {
      if (device.club_id && device.court_id) {
        return {
          label: `Cancha ${device.court_number || '?'} - ${device.club_name || 'Club'}`,
          sublabel: 'Modo Renta (todos los usuarios en cancha)',
          icon: Building2,
          color: 'text-blue-400'
        };
      }
      return {
        label: 'Sin cancha asignada',
        sublabel: 'Configura club y cancha',
        icon: AlertCircle,
        color: 'text-orange-400'
      };
    } else {
      if (device.assigned_user_id) {
        return {
          label: device.assigned_user_name || 'Usuario',
          sublabel: device.assigned_user_email || device.assigned_user_phone || 'Modo Personal',
          icon: User,
          color: 'text-green-400'
        };
      }
      return {
        label: 'Sin usuario asignado',
        sublabel: 'Asigna un usuario',
        icon: UserPlus,
        color: 'text-orange-400'
      };
    }
  };

  const assignment = getAssignmentDisplay();
  const AssignmentIcon = assignment.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`bg-card rounded-xl p-4 border ${isAssigned ? 'border-green-500/30' : 'border-orange-500/30'}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${isRental ? 'bg-blue-500/20' : 'bg-primary/20'}`}>
            <Radio className={`w-4 h-4 ${isRental ? 'text-blue-400' : 'text-primary'}`} />
          </div>
          <div>
            <h3 className="font-semibold">{device.device_name || 'Botón Shelly'}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isRental ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
            }`}>
              {isRental ? 'Renta' : 'Personal'}
            </span>
          </div>
        </div>
        <div className={`w-2 h-2 rounded-full ${device.is_active !== false ? 'bg-green-400' : 'bg-gray-400'}`} />
      </div>

      {/* Assignment Info */}
      <div className="flex items-center gap-2 mb-3 p-2 bg-secondary/50 rounded-lg">
        <AssignmentIcon className={`w-4 h-4 ${assignment.color}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${assignment.color}`}>{assignment.label}</p>
          <p className="text-xs text-muted-foreground truncate">{assignment.sublabel}</p>
        </div>
      </div>

      {/* Device Info */}
      <div className="space-y-2 text-xs text-muted-foreground mb-3">
        <div className="flex items-center gap-2">
          <Hash className="w-3 h-3" />
          <span className="font-mono truncate">{device.device_id}</span>
        </div>
        {device.club_name && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            <span>{device.club_name}</span>
          </div>
        )}
      </div>

      {/* Webhook URL with Setup Instructions */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-muted-foreground">Webhook URL:</p>
          <Button 
            variant="link" 
            size="sm" 
            className="h-5 px-1 text-xs text-yellow-400 hover:text-yellow-300"
            onClick={() => onShowInstructions && onShowInstructions(device)}
          >
            ⚙️ Cómo configurar
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <code className="flex-1 text-xs bg-secondary p-1 rounded truncate">{device.webhook_url}</code>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyWebhookUrl}>
            {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
          </Button>
        </div>
        
        {/* Configuration Warning */}
        {!device.last_signal_at && (
          <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-400">
            ⚠️ <strong>Pendiente de configurar:</strong> Este botón aún no ha enviado señales. Configura el webhook en la app Shelly.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => setShowEditModal(true)}>
          <Edit className="w-4 h-4" />
          Editar
        </Button>
        <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => onTest(device)}>
          <Play className="w-4 h-4" />
          Probar
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onViewLogs(device)}>
          <Activity className="w-4 h-4" />
        </Button>
        {showDeleteConfirm ? (
          <div className="flex items-center gap-1">
            <Button variant="destructive" size="icon" className="h-8 w-8" onClick={() => { onDelete(device.device_id); setShowDeleteConfirm(false); }}>
              <Check className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowDeleteConfirm(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Editar Botón Shelly
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-secondary/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground font-mono">{device.device_id}</p>
            </div>
            
            <div>
              <Label>Nombre del botón</Label>
              <Input
                placeholder="ej: Botón Juan García"
                value={editData.device_name}
                onChange={(e) => setEditData({ ...editData, device_name: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Tipo de botón</Label>
              <Select value={editData.button_type} onValueChange={(val) => setEditData({ ...editData, button_type: val })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-400" />
                      Personal (asignado a usuario)
                    </span>
                  </SelectItem>
                  <SelectItem value="rental">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-400" />
                      Renta (todos en la cancha)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Club</Label>
              <Select value={editData.club_id} onValueChange={handleEditClubChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona un club" />
                </SelectTrigger>
                <SelectContent>
                  {clubs.map(club => (
                    <SelectItem key={club.id} value={club.id}>
                      {club.name} ({club.city})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {editSelectedClub && (
              <div>
                <Label>Cancha</Label>
                <Select value={editData.court_id} onValueChange={(val) => setEditData({ ...editData, court_id: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona una cancha" />
                  </SelectTrigger>
                  <SelectContent>
                    {editSelectedClub.courts?.map(court => (
                      <SelectItem key={court.id} value={court.id}>
                        Cancha {court.court_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editData.is_active}
                onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_active">Dispositivo activo</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit}>
              <Check className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// ===========================================
// USER SEARCH COMPONENT
// ===========================================
const UserSearchAssign = ({ device, onAssign, onClose }) => {
  const [searchQuery, setSearchQuery] = useState({ email: '', phone: '', name: '', user_id: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [creating, setCreating] = useState(false);

  const handleSearch = useCallback(async () => {
    const hasQuery = searchQuery.email || searchQuery.phone || searchQuery.name || searchQuery.user_id;
    if (!hasQuery) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await adminAPI.searchUsersForShelly(searchQuery);
      setSearchResults(res.data.users || []);
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Error buscando usuarios');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Debounced search on input change
  useEffect(() => {
    const timeout = setTimeout(handleSearch, 300);
    return () => clearTimeout(timeout);
  }, [handleSearch]);

  const handleCreateUser = async () => {
    if (!newUser.first_name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!newUser.email && !newUser.phone) {
      toast.error('Debes proporcionar email o teléfono');
      return;
    }
    
    setCreating(true);
    try {
      const res = await adminAPI.createUserForShelly(newUser);
      toast.success(`Usuario creado. Contraseña temporal: ${res.data.temporary_password}`);
      // Auto-assign the new user
      await onAssign(device.device_id, res.data.user.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando usuario');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      {!showCreateUser ? (
        <>
          {/* Search Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1 text-xs">
                <Mail className="w-3 h-3" /> Email
              </Label>
              <Input
                placeholder="juan@email.com"
                value={searchQuery.email}
                onChange={(e) => setSearchQuery({ ...searchQuery, email: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs">
                <Phone className="w-3 h-3" /> Teléfono
              </Label>
              <Input
                placeholder="+52 555..."
                value={searchQuery.phone}
                onChange={(e) => setSearchQuery({ ...searchQuery, phone: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs">
                <User className="w-3 h-3" /> Nombre
              </Label>
              <Input
                placeholder="Juan García"
                value={searchQuery.name}
                onChange={(e) => setSearchQuery({ ...searchQuery, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs">
                <Hash className="w-3 h-3" /> ID Usuario
              </Label>
              <Input
                placeholder="uuid..."
                value={searchQuery.user_id}
                onChange={(e) => setSearchQuery({ ...searchQuery, user_id: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          {/* Search Results */}
          <div className="max-h-48 overflow-y-auto space-y-2">
            {searching && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            )}
            
            {!searching && searchResults.length === 0 && (searchQuery.email || searchQuery.phone || searchQuery.name) && (
              <p className="text-sm text-muted-foreground text-center py-4">No se encontraron usuarios</p>
            )}
            
            {searchResults.map((user) => (
              <div 
                key={user.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  user.can_assign_more ? 'border-border hover:border-primary cursor-pointer' : 'border-red-500/30 opacity-60'
                }`}
                onClick={() => user.can_assign_more && onAssign(device.device_id, user.id).then(onClose)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{user.display_name}</p>
                    <p className="text-xs text-muted-foreground">{user.email || user.phone}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-xs ${user.can_assign_more ? 'text-green-400' : 'text-red-400'}`}>
                    {user.button_count}/2 botones
                  </span>
                  {!user.can_assign_more && (
                    <p className="text-xs text-red-400">Máximo alcanzado</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Create User Button */}
          <Button variant="outline" className="w-full" onClick={() => setShowCreateUser(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Crear nuevo usuario
          </Button>
        </>
      ) : (
        <>
          {/* Create User Form */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={() => setShowCreateUser(false)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-medium">Crear nuevo usuario</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input
                  placeholder="Juan"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Apellido</Label>
                <Input
                  placeholder="García"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="juan@email.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Teléfono</Label>
              <Input
                placeholder="+52 555 123 4567"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <p className="text-xs text-muted-foreground">
              * Se generará una contraseña temporal que podrás compartir con el usuario.
            </p>
          </div>
          
          <Button onClick={handleCreateUser} disabled={creating} className="w-full">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Crear y Asignar
          </Button>
        </>
      )}
    </div>
  );
};

// ===========================================
// SIGNAL LOGS COMPONENT
// ===========================================
const SignalLogsModal = ({ device, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await adminAPI.getShellySignalLogs(device.device_id, 30);
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error('Error fetching logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [device.device_id]);

  const getResultBadge = (result) => {
    const configs = {
      success_rental: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Éxito (Renta)' },
      success_personal: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Éxito (Personal)' },
      test_success: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Prueba OK' },
      device_not_registered: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'No registrado' },
      device_not_assigned: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Sin asignar' },
      event_not_mapped: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Evento ignorado' },
      no_users_on_court: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Sin usuarios' },
    };
    const config = configs[result] || { bg: 'bg-gray-500/20', text: 'text-gray-400', label: result || 'Desconocido' };
    return <span className={`px-2 py-0.5 rounded text-xs ${config.bg} ${config.text}`}>{config.label}</span>;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Historial de Señales
          </DialogTitle>
          <DialogDescription>
            {device.device_name || device.device_id}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No hay señales registradas</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Wifi className={`w-4 h-4 ${log.result?.includes('success') ? 'text-green-400' : 'text-muted-foreground'}`} />
                    <div>
                      <p className="text-sm font-medium">{log.event_type || 'Señal'}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString('es-MX')}
                      </p>
                    </div>
                  </div>
                  {getResultBadge(log.result)}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================
// TEST BUTTON MODAL
// ===========================================
const TestButtonModal = ({ device, onClose }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTestSignal = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminAPI.testShellySignal(device.device_id);
      setTestResult({ success: true, message: res.data.message, timestamp: res.data.timestamp });
      toast.success('✅ Señal recibida correctamente');
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Error en prueba' });
      toast.error('❌ Error en prueba de señal');
    } finally {
      setTesting(false);
    }
  };

  const handleTestWithClip = async (eventType) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminAPI.testShellyAdminMode(device.device_id, eventType, null);
      setTestResult({ 
        success: true, 
        message: res.data.message,
        clip_id: res.data.clip_id,
        action: res.data.action
      });
      toast.success(`✅ Clip creado: ${res.data.action}`);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Error creando clip' });
      toast.error('❌ Error creando clip de prueba');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="w-5 h-5 text-primary" />
            Probar Botón Shelly
          </DialogTitle>
          <DialogDescription>
            {device.device_name || device.device_id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Signal Test Only */}
          <div className="p-4 border border-border rounded-lg">
            <h4 className="font-medium mb-2">Prueba de Señal (sin crear clip)</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Verifica que el botón está enviando señales correctamente.
            </p>
            <Button onClick={handleTestSignal} disabled={testing} className="w-full">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
              Probar Conexión
            </Button>
          </div>

          {/* Test with Clip */}
          <div className="p-4 border border-border rounded-lg">
            <h4 className="font-medium mb-2">Prueba con Clip</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Simula una presión del botón y crea un clip de prueba.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => handleTestWithClip('single_push')} disabled={testing}>
                1 Press (30s)
              </Button>
              <Button variant="outline" onClick={() => handleTestWithClip('double_push')} disabled={testing}>
                2 Press (60s 2x)
              </Button>
            </div>
          </div>

          {/* Result */}
          {testResult && (
            <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400" />
                )}
                <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                  {testResult.message}
                </span>
              </div>
              {testResult.timestamp && (
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(testResult.timestamp).toLocaleString('es-MX')}
                </p>
              )}
              {testResult.clip_id && (
                <p className="text-xs text-muted-foreground mt-1">
                  Clip ID: {testResult.clip_id.substring(0, 8)}...
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================
// MAIN COMPONENT
// ===========================================
export default function AdminShelly() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(null); // device to assign
  const [showLogsModal, setShowLogsModal] = useState(null); // device to view logs
  const [showTestModal, setShowTestModal] = useState(null); // device to test
  const [showInstructionsModal, setShowInstructionsModal] = useState(null); // device for setup instructions
  
  // New device form
  const [newDevice, setNewDevice] = useState({
    device_id: '',
    device_name: '',
    button_type: 'personal',
    // For rental buttons
    club_id: '',
    court_id: '',
    // For personal buttons
    assigned_user_id: ''
  });
  const [selectedClub, setSelectedClub] = useState(null);
  // User search for add modal
  const [addUserSearch, setAddUserSearch] = useState({ email: '', phone: '', name: '', user_id: '' });
  const [addUserResults, setAddUserResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addSelectedUser, setAddSelectedUser] = useState(null);
  const [showAddCreateUser, setShowAddCreateUser] = useState(false);
  const [addNewUser, setAddNewUser] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [addCreating, setAddCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [devicesRes, clubsRes] = await Promise.all([
        adminAPI.getShellyDevices(),
        adminAPI.getClubs()
      ]);
      setDevices(devicesRes.data.devices || []);
      setClubs(clubsRes.data.clubs || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter devices
  const filteredDevices = useMemo(() => {
    return devices.filter(device => {
      // Search filter
      const matchesSearch = !searchQuery || 
        device.device_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.device_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.assigned_user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.club_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Type filter
      const matchesType = filterType === 'all' ||
        (filterType === 'personal' && device.button_type !== 'rental') ||
        (filterType === 'rental' && device.button_type === 'rental') ||
        (filterType === 'unassigned' && !device.assigned_user_id && device.button_type !== 'rental');
      
      return matchesSearch && matchesType;
    });
  }, [devices, searchQuery, filterType]);

  // Stats
  const stats = useMemo(() => ({
    total: devices.length,
    personal: devices.filter(d => d.button_type !== 'rental').length,
    rental: devices.filter(d => d.button_type === 'rental').length,
    assigned: devices.filter(d => d.assigned_user_id || (d.button_type === 'rental' && d.court_id)).length,
    unassigned: devices.filter(d => !d.assigned_user_id && d.button_type !== 'rental').length
  }), [devices]);

  // Handlers
  const handleClubSelect = (clubId) => {
    const club = clubs.find(c => c.id === clubId);
    setSelectedClub(club);
    setNewDevice({ ...newDevice, club_id: clubId, court_id: '' });
  };

  // User search for add modal (personal buttons)
  const handleAddUserSearch = useCallback(async () => {
    const hasQuery = addUserSearch.email || addUserSearch.phone || addUserSearch.name || addUserSearch.user_id;
    if (!hasQuery) {
      setAddUserResults([]);
      return;
    }
    
    setAddSearching(true);
    try {
      const res = await adminAPI.searchUsersForShelly(addUserSearch);
      setAddUserResults(res.data.users || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setAddSearching(false);
    }
  }, [addUserSearch]);

  useEffect(() => {
    const timeout = setTimeout(handleAddUserSearch, 300);
    return () => clearTimeout(timeout);
  }, [handleAddUserSearch]);

  const handleSelectUserForAdd = (user) => {
    if (!user.can_assign_more) {
      toast.error(`${user.display_name} ya tiene 2 botones asignados (máximo)`);
      return;
    }
    setAddSelectedUser(user);
    setNewDevice({ ...newDevice, assigned_user_id: user.id });
  };

  const handleCreateUserForAdd = async () => {
    if (!addNewUser.first_name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!addNewUser.email && !addNewUser.phone) {
      toast.error('Debes proporcionar email o teléfono');
      return;
    }
    
    setAddCreating(true);
    try {
      const res = await adminAPI.createUserForShelly(addNewUser);
      toast.success(`Usuario creado. Contraseña temporal: ${res.data.temporary_password}`);
      setAddSelectedUser(res.data.user);
      setNewDevice({ ...newDevice, assigned_user_id: res.data.user.id });
      setShowAddCreateUser(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando usuario');
    } finally {
      setAddCreating(false);
    }
  };

  const handleAddDevice = async () => {
    if (!newDevice.device_id) {
      toast.error('El ID del dispositivo es requerido');
      return;
    }
    
    // Validate based on button type
    if (newDevice.button_type === 'rental') {
      if (!newDevice.club_id || !newDevice.court_id) {
        toast.error('Para botones de renta, debes seleccionar club y cancha');
        return;
      }
    } else {
      // Personal button - need either selected user OR search data
      const hasSearchData = addUserSearch.email || addUserSearch.phone || addUserSearch.name;
      if (!newDevice.assigned_user_id && !hasSearchData) {
        toast.error('Para botones personales, debes proporcionar al menos un dato del usuario (email, teléfono o nombre)');
        return;
      }
    }
    
    try {
      let userId = newDevice.assigned_user_id;
      
      // If no user selected but we have search data, create the user first
      if (newDevice.button_type === 'personal' && !userId) {
        const userData = {};
        
        if (addUserSearch.email) {
          userData.email = addUserSearch.email;
          userData.first_name = addUserSearch.email.split('@')[0]; // Use email prefix as name
        } else if (addUserSearch.phone) {
          userData.phone = addUserSearch.phone;
          userData.first_name = 'Usuario ' + addUserSearch.phone.slice(-4); // Use last 4 digits
        } else if (addUserSearch.name) {
          const nameParts = addUserSearch.name.trim().split(' ');
          userData.first_name = nameParts[0];
          userData.last_name = nameParts.slice(1).join(' ') || '';
          // Generate a placeholder email or phone will be needed
          userData.email = `${nameParts[0].toLowerCase()}${Date.now()}@placeholder.clipper.app`;
        }
        
        // Create user via API
        try {
          const createRes = await adminAPI.createUserForShelly(userData);
          userId = createRes.data.user.id;
          const tempPassword = createRes.data.temporary_password;
          toast.success(`Usuario creado. Contraseña temporal: ${tempPassword}`, { duration: 10000 });
        } catch (createErr) {
          // If user already exists, try to find them
          if (createErr.response?.status === 400 && createErr.response?.data?.detail?.includes('existe')) {
            // Search for existing user
            const searchRes = await adminAPI.searchUsersForShelly(addUserSearch);
            if (searchRes.data.users && searchRes.data.users.length > 0) {
              const existingUser = searchRes.data.users[0];
              if (!existingUser.can_assign_more) {
                toast.error(`${existingUser.display_name} ya tiene 2 botones asignados (máximo)`);
                return;
              }
              userId = existingUser.id;
              toast.info(`Usuario existente encontrado: ${existingUser.display_name}`);
            } else {
              throw createErr;
            }
          } else {
            throw createErr;
          }
        }
      }
      
      // Prepare data based on button type
      const deviceData = {
        device_id: newDevice.device_id,
        device_name: newDevice.device_name,
        button_type: newDevice.button_type
      };
      
      if (newDevice.button_type === 'rental') {
        deviceData.club_id = newDevice.club_id;
        deviceData.court_id = newDevice.court_id;
      } else {
        deviceData.assigned_user_id = userId;
      }
      
      await adminAPI.registerShellyDevice(deviceData);
      toast.success('Dispositivo registrado exitosamente ✓');
      setShowAddModal(false);
      // Reset all form state
      setNewDevice({ device_id: '', device_name: '', button_type: 'personal', club_id: '', court_id: '', assigned_user_id: '' });
      setSelectedClub(null);
      setAddSelectedUser(null);
      setAddUserSearch({ email: '', phone: '', name: '', user_id: '' });
      setAddUserResults([]);
      setShowAddCreateUser(false);
      setAddNewUser({ first_name: '', last_name: '', email: '', phone: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error registrando dispositivo');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      await adminAPI.deleteShellyDevice(deviceId);
      toast.success('Dispositivo eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error eliminando dispositivo');
    }
  };

  const handleEditDevice = async (deviceId, data) => {
    try {
      await adminAPI.updateShellyDevice(deviceId, data);
      toast.success('Dispositivo actualizado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando dispositivo');
    }
  };

  const handleAssignUser = async (deviceId, userId) => {
    try {
      await adminAPI.assignShellyToUser(deviceId, userId);
      toast.success('Usuario asignado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error asignando usuario');
      throw err;
    }
  };

  const handleUnassignUser = async (deviceId) => {
    try {
      await adminAPI.unassignShelly(deviceId);
      toast.success('Usuario desasignado');
      fetchData();
    } catch (err) {
      toast.error('Error desasignando usuario');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Radio className="w-5 h-5 text-primary" />
                  Botones Shelly
                </h1>
                <p className="text-sm text-muted-foreground">Gestiona botones para replays</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={fetchData} variant="outline" disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-card rounded-xl p-3 border border-border text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-purple-400">{stats.personal}</p>
            <p className="text-xs text-muted-foreground">Personal</p>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-blue-400">{stats.rental}</p>
            <p className="text-xs text-muted-foreground">Renta</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-400">{stats.assigned}</p>
            <p className="text-xs text-muted-foreground">Asignados</p>
          </div>
          <div className="bg-orange-500/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-orange-400">{stats.unassigned}</p>
            <p className="text-xs text-muted-foreground">Sin asignar</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre, ID, usuario, club..."
              className="pl-9"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filtrar por tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
              <SelectItem value="rental">Renta</SelectItem>
              <SelectItem value="unassigned">Sin asignar</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Device Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="text-center py-12">
            <Radio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No hay dispositivos</h3>
            <p className="text-muted-foreground mb-4">Agrega tu primer botón Shelly</p>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar Botón
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredDevices.map((device) => (
                <DeviceCard
                  key={device.id || device.device_id}
                  device={device}
                  clubs={clubs}
                  onDelete={handleDeleteDevice}
                  onTest={setShowTestModal}
                  onAssign={() => setShowAssignModal(device)}
                  onUnassign={handleUnassignUser}
                  onEdit={handleEditDevice}
                  onViewLogs={setShowLogsModal}
                  onShowInstructions={setShowInstructionsModal}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) {
          // Reset form on close
          setNewDevice({ device_id: '', device_name: '', button_type: 'personal', club_id: '', court_id: '', assigned_user_id: '' });
          setSelectedClub(null);
          setAddSelectedUser(null);
          setAddUserSearch({ email: '', phone: '', name: '', user_id: '' });
          setAddUserResults([]);
          setShowAddCreateUser(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Agregar Botón Shelly
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Device ID */}
            <div>
              <Label>ID del dispositivo *</Label>
              <Input
                placeholder="SHELLYBLUBTN-AABBCCDD"
                value={newDevice.device_id}
                onChange={(e) => setNewDevice({ ...newDevice, device_id: e.target.value })}
                className="mt-1 font-mono"
              />
            </div>

            {/* Device Name */}
            <div>
              <Label>Nombre del botón</Label>
              <Input
                placeholder="ej: Botón Juan García"
                value={newDevice.device_name}
                onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Button Type */}
            <div>
              <Label>Tipo de botón *</Label>
              <Select 
                value={newDevice.button_type} 
                onValueChange={(val) => {
                  setNewDevice({ ...newDevice, button_type: val, club_id: '', court_id: '', assigned_user_id: '' });
                  setSelectedClub(null);
                  setAddSelectedUser(null);
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-400" />
                      Personal (asignado a usuario)
                    </span>
                  </SelectItem>
                  <SelectItem value="rental">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-400" />
                      Renta (todos en la cancha)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* RENTAL: Club + Court Selection */}
            {newDevice.button_type === 'rental' && (
              <>
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Los botones de renta generan clips para <strong>todos los usuarios</strong> con sesión activa en la cancha.
                  </p>
                </div>
                
                <div>
                  <Label>Club *</Label>
                  <Select value={newDevice.club_id} onValueChange={handleClubSelect}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecciona un club" />
                    </SelectTrigger>
                    <SelectContent>
                      {clubs.map(club => (
                        <SelectItem key={club.id} value={club.id}>{club.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedClub && (
                  <div>
                    <Label>Cancha *</Label>
                    <Select value={newDevice.court_id} onValueChange={(val) => setNewDevice({ ...newDevice, court_id: val })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecciona una cancha" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedClub.courts?.map(court => (
                          <SelectItem key={court.id} value={court.id}>Cancha {court.court_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* PERSONAL: User Search & Selection */}
            {newDevice.button_type === 'personal' && (
              <>
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-purple-400">
                    <User className="w-4 h-4 inline mr-1" />
                    Los botones personales generan clips <strong>solo para el usuario asignado</strong>. Máximo 2 botones por usuario.
                  </p>
                </div>

                {/* Helper Message */}
                <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-xs text-yellow-400 text-center">
                    💡 <strong>Solo necesitas llenar UN campo</strong> (email, teléfono o nombre) para asignar el botón
                  </p>
                </div>

                {/* Selected User Display */}
                {addSelectedUser && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-green-400" />
                      <div>
                        <p className="font-medium text-green-400">{addSelectedUser.display_name}</p>
                        <p className="text-xs text-muted-foreground">{addSelectedUser.email || addSelectedUser.phone}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setAddSelectedUser(null);
                      setNewDevice({ ...newDevice, assigned_user_id: '', user_identifier: '' });
                    }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Simplified User Input - One field is enough */}
                {!addSelectedUser && (
                  <>
                    <div className="space-y-3">
                      {/* Email field */}
                      <div>
                        <Label className="flex items-center gap-1 text-xs">
                          <Mail className="w-3 h-3" /> Email del usuario
                        </Label>
                        <Input
                          placeholder="juan@email.com"
                          value={addUserSearch.email}
                          onChange={(e) => setAddUserSearch({ ...addUserSearch, email: e.target.value, phone: '', name: '', user_id: '' })}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="text-center text-xs text-muted-foreground">— o —</div>
                      
                      {/* Phone field */}
                      <div>
                        <Label className="flex items-center gap-1 text-xs">
                          <Phone className="w-3 h-3" /> Teléfono del usuario
                        </Label>
                        <Input
                          placeholder="+52 555 123 4567"
                          value={addUserSearch.phone}
                          onChange={(e) => setAddUserSearch({ ...addUserSearch, phone: e.target.value, email: '', name: '', user_id: '' })}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="text-center text-xs text-muted-foreground">— o —</div>
                      
                      {/* Name field */}
                      <div>
                        <Label className="flex items-center gap-1 text-xs">
                          <User className="w-3 h-3" /> Nombre del usuario
                        </Label>
                        <Input
                          placeholder="Juan García"
                          value={addUserSearch.name}
                          onChange={(e) => setAddUserSearch({ ...addUserSearch, name: e.target.value, email: '', phone: '', user_id: '' })}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Search Results - Auto display when typing */}
                    {(addUserSearch.email || addUserSearch.phone || addUserSearch.name) && (
                      <div className="max-h-40 overflow-y-auto space-y-2 border border-border rounded-lg p-2">
                        {addSearching && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                          </div>
                        )}
                        
                        {!addSearching && addUserResults.length === 0 && (
                          <div className="text-center py-3">
                            <p className="text-sm text-muted-foreground mb-2">No se encontró usuario existente</p>
                            <p className="text-xs text-green-400">✓ Se creará automáticamente al guardar</p>
                          </div>
                        )}
                        
                        {addUserResults.length > 0 && (
                          <p className="text-xs text-muted-foreground mb-2">Usuarios encontrados (click para seleccionar):</p>
                        )}
                        
                        {addUserResults.map((user) => (
                          <div 
                            key={user.id}
                            className={`flex items-center justify-between p-2 rounded-lg border text-sm ${
                              user.can_assign_more ? 'border-border hover:border-primary cursor-pointer hover:bg-primary/5' : 'border-red-500/30 opacity-60'
                            }`}
                            onClick={() => handleSelectUserForAdd(user)}
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                                <User className="w-3 h-3 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{user.display_name}</p>
                                <p className="text-xs text-muted-foreground">{user.email || user.phone}</p>
                              </div>
                            </div>
                            <span className={`text-xs ${user.can_assign_more ? 'text-green-400' : 'text-red-400'}`}>
                              {user.button_count}/2
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancelar</Button>
            <Button onClick={handleAddDevice} disabled={
              !newDevice.device_id || 
              (newDevice.button_type === 'rental' && (!newDevice.club_id || !newDevice.court_id)) ||
              (newDevice.button_type === 'personal' && !addSelectedUser && !addUserSearch.email && !addUserSearch.phone && !addUserSearch.name)
            }>
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Modal */}
      {showAssignModal && (
        <Dialog open={true} onOpenChange={() => setShowAssignModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Asignar Usuario
              </DialogTitle>
              <DialogDescription>
                {showAssignModal.device_name || showAssignModal.device_id}
              </DialogDescription>
            </DialogHeader>

            <UserSearchAssign
              device={showAssignModal}
              onAssign={handleAssignUser}
              onClose={() => setShowAssignModal(null)}
            />

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignModal(null)}>Cancelar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Signal Logs Modal */}
      {showLogsModal && (
        <SignalLogsModal device={showLogsModal} onClose={() => setShowLogsModal(null)} />
      )}

      {/* Test Button Modal */}
      {showTestModal && (
        <TestButtonModal device={showTestModal} onClose={() => setShowTestModal(null)} />
      )}

      {/* Setup Instructions Modal */}
      {showInstructionsModal && (
        <Dialog open={true} onOpenChange={() => setShowInstructionsModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="w-5 h-5 text-primary" />
                Configurar Botón Shelly
              </DialogTitle>
              <DialogDescription>
                Sigue estos pasos para conectar tu botón físico con el sistema
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Step 1 */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                  <div>
                    <p className="font-medium text-blue-400">Descarga la App Shelly</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Descarga <strong>Shelly Smart Control</strong> desde App Store o Google Play y crea una cuenta si no tienes una.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                  <div>
                    <p className="font-medium text-purple-400">Agrega tu Botón Shelly</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      En la app, toca <strong>&quot;Add Device&quot;</strong> → busca <strong>&quot;Shelly BLU Button&quot;</strong> → sigue las instrucciones para emparejarlo via Bluetooth.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                  <div>
                    <p className="font-medium text-green-400">Configura el Webhook</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      En la app Shelly, ve a tu botón → <strong>Settings</strong> → <strong>Actions</strong> → <strong>Button Pushed</strong> → selecciona <strong>&quot;URL Action&quot;</strong> y pega esta URL:
                    </p>
                    <div className="mt-2 p-2 bg-black/50 rounded flex items-center gap-2">
                      <code className="flex-1 text-xs text-green-300 break-all">{showInstructionsModal.webhook_url}</code>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(showInstructionsModal.webhook_url);
                          toast.success('URL copiada');
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-yellow-500 text-white flex items-center justify-center text-sm font-bold shrink-0">4</div>
                  <div>
                    <p className="font-medium text-yellow-400">Configura Doble Click (Opcional)</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Para clips de 60 segundos a doble velocidad, ve a <strong>Actions</strong> → <strong>Double Push</strong> → configura la misma URL.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold shrink-0">5</div>
                  <div>
                    <p className="font-medium text-orange-400">¡Prueba tu Botón!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Presiona el botón físico. Si está bien configurado, verás la señal en el <strong>Log de Señales</strong> aquí en la plataforma.
                    </p>
                  </div>
                </div>
              </div>

              {/* Important Notes */}
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="font-medium text-red-400 text-sm flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Notas Importantes:
                </p>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  <li>El botón debe estar conectado a internet via Shelly Cloud</li>
                  <li>El botón <strong>&quot;Probar&quot;</strong> de esta página es para pruebas de SIMULACIÓN del administrador</li>
                  <li>Las señales REALES solo llegan cuando presionas el botón físico</li>
                  <li>Si el botón no envía señales, verifica tu conexión WiFi y la configuración de la app Shelly</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={() => setShowInstructionsModal(null)}>Entendido</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
